import { getPlayableAssetUrl } from "../../utils/assetUrl";
import { deliveryLabel } from "../../utils/deliveryLabel";
import { formatDisplayDateTime } from "../../utils/helperFunctions";

// Extract the file id from any common Google Drive link shape (file/d/, open?id=, uc?id=).
const getDriveFileId = (url) => {
  const m =
    /drive\.google\.com\/file\/d\/([^/?#]+)/i.exec(url) ||
    /drive\.google\.com\/(?:open|uc)\?(?:[^#]*&)?id=([^&#]+)/i.exec(url);
  return m ? m[1] : null;
};

/**
 * One delivered video, same source as Ad Moderation (getPlayableAssetUrl): the
 * S3/CDN copy (s3_asset_url, written by the drive→S3 migration task) plays in a
 * native <video>; a Drive-only link isn't a video stream, so it embeds via the
 * drive /preview iframe instead. Raw link always shown below the player.
 *
 * Shared by the QC review flow and the read-only view for users without
 * ad_qc.review, so both show the same player and the same label.
 */
const DeliveredVideo = ({ delivery }) => {
  const s3 = (delivery.s3_asset_url || "").trim();
  const raw = (delivery.ad_asset_url || "").trim();
  const src = getPlayableAssetUrl(delivery);
  if (!src) return null;
  const driveId = !s3 ? getDriveFileId(raw) : null;
  const title = deliveryLabel(delivery);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      {/* Marked only when this delivery itself was sent back. Approval is still
          one request-level verdict, so there is no per-delivery "approved" chip
          — but a delivery in changes_requested is genuinely different from its
          siblings and the reviewer needs to see which one at a glance. */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
        <span className="text-xs font-medium text-gray-700 truncate">
          {title}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {delivery.qc_status === "changes_requested" && (
            <span className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 whitespace-nowrap">
              Sent back
            </span>
          )}
          {delivery.created_on && (
            <span className="text-[11px] text-gray-400 whitespace-nowrap">
              {formatDisplayDateTime(delivery.created_on)}
            </span>
          )}
        </div>
      </div>
      {driveId ? (
        <iframe
          title={title}
          src={`https://drive.google.com/file/d/${driveId}/preview`}
          className="w-full h-[34rem] border-0 bg-black"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      ) : (
        <video src={src} controls className="w-full max-h-[34rem] bg-black" />
      )}
      <div className="px-3 py-1.5">
        <a
          href={raw || src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primaryPurple hover:underline break-all"
        >
          {raw || src}
        </a>
      </div>
    </div>
  );
};

export default DeliveredVideo;
